import { toast } from "sonner";
import React, { lazy, Suspense, useState } from 'react';
import { useNavigation } from '../../context/NavigationContext';
import { useSupabase } from '../../context/SupabaseContext';
import { useSettings } from '../../context/SettingsContext';
import { contactService } from '../../services/contactService';
import { contactGroupService } from '../../services/contactGroupService';
import { WorkerRoleCombobox } from '../contacts/WorkerRoleCombobox';
import { DEFAULT_WORKER_ROLES } from '@/app/lib/workerRoles';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { RefreshCcw } from 'lucide-react';
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "../ui/accordion";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Switch } from "../ui/switch";

const TransactionForm = lazy(() => import('../transactions/TransactionForm').then(m => ({ default: m.TransactionForm })));
const PurchaseForm = lazy(() => import('../purchases/PurchaseForm').then(m => ({ default: m.PurchaseForm })));
const SaleForm = lazy(() => import('../sales/SaleForm').then(m => ({ default: m.SaleForm })));
const EnhancedProductForm = lazy(() => import('../products/EnhancedProductForm').then(m => ({ default: m.EnhancedProductForm })));
const PackingEntryModal = lazy(() => import('../transactions/PackingEntryModal').then(m => ({ default: m.PackingEntryModal })));

export const GlobalDrawer = () => {
  const { activeDrawer, openDrawer, closeDrawer, drawerData, parentDrawer, setCreatedProduct, packingModalOpen, closePackingModal, packingModalData } = useNavigation();
  const { companyId, user } = useSupabase();
  const { inventorySettings } = useSettings();
  const enablePacking = inventorySettings.enablePacking;
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [saving, setSaving] = useState(false);

  const isOpen = activeDrawer !== 'none';
  const handleOpenChange = (open: boolean) => {
    if (!open) closeDrawer();
  };
  
  // Determine if we should show parent drawer (Sale/Purchase) when child drawer (Contact) is open
  const shouldShowParentDrawer = (drawerType: 'addSale' | 'addPurchase' | 'edit-sale' | 'edit-purchase') => {
    // Show if it's the active drawer OR if it's the parent of the current drawer
    return activeDrawer === drawerType || parentDrawer === drawerType;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!companyId || !user) {
      toast.error('Company ID or user not found. Please login again.');
      return;
    }

    setSaving(true);

    try {
      if (activeDrawer === 'addContact') {
        // Contact form submission is handled in ContactFormContent
        // This is just a fallback
        toast.success('Contact created successfully');
        closeDrawer();
      } else {
        // For other drawer types (Product, etc.)
        const action = 'Product';
        toast.success(`${action} created successfully`);
        closeDrawer();
      }
    } catch (error: any) {
      console.error('Submit error:', error);
      toast.error(error.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Determine width and side based on content type and device
  const isSale = activeDrawer === 'addSale' || activeDrawer === 'edit-sale';
  const isPurchase = activeDrawer === 'addPurchase' || activeDrawer === 'edit-purchase';
  const isProduct = activeDrawer === 'addProduct' || activeDrawer === 'edit-product';
  const isContact = activeDrawer === 'addContact';
  const side = isMobile ? "bottom" : "right";
  
  // Check if we have a nested drawer (Contact opened from Sale/Purchase)
  const hasNestedDrawer = isContact && parentDrawer && (parentDrawer === 'addSale' || parentDrawer === 'addPurchase' || parentDrawer === 'edit-sale' || parentDrawer === 'edit-purchase');
  // Nested Product drawer: Add Product opened from Sale/Purchase – keep Sale/Purchase visible behind
  const hasNestedProductDrawer = isProduct && parentDrawer && (parentDrawer === 'addSale' || parentDrawer === 'addPurchase' || parentDrawer === 'edit-sale' || parentDrawer === 'edit-purchase');
  
  // Custom classes for the sheet content
  // IMPORTANT: Responsive widths, isolated from global layout
  let contentClasses = "border-l border-border bg-background text-foreground p-0 gap-0 "; 
  
  if (isMobile) {
    contentClasses += "h-[90vh] rounded-t-xl border-t"; // Bottom sheet style
  } else {
    contentClasses += "h-full flex flex-col ";
    if (isSale || isPurchase) {
      // Responsive width for Sale/Purchase: full width on mobile/tablet, 1200px on desktop (lg+)
      // Override base Sheet's sm:max-w-md (448px) so drawer gets actual size
      contentClasses += "!max-w-none w-full lg:w-[1200px] lg:max-w-[1200px]";
    } else if (isProduct) {
       contentClasses += "!w-[900px] !max-w-[95vw] sm:!max-w-[900px] flex flex-col min-h-0"; // Product form + variations table; flex so form can scroll
    } else if (isContact) {
      // Contact form: Fixed 580-600px width; min-h-0 + overflow-hidden so inner form can scroll
      contentClasses += "!w-[580px] !max-w-[600px] sm:!max-w-[600px] min-h-0 overflow-hidden flex flex-col";
    } else {
      contentClasses += "w-[400px] sm:w-[540px]"; // Standard for simple forms
    }
  }
  
  // For nested Contact / Add Product drawers, ensure higher z-index than parent Sale/Purchase sheet
  if (hasNestedDrawer || hasNestedProductDrawer) {
    contentClasses += " !z-[70]"; // Higher z-index than parent drawer (z-50 / z-60)
  }

  // Render parent drawer content (Sale/Purchase only)
  // CRITICAL: Use display: none/block instead of conditional rendering to prevent unmounting
  // This preserves component state (like customerId) when contact drawer opens/closes
  const renderParentContent = () => {
    return (
      <>
        {/* Sale Form - Add */}
        <div style={{ display: shouldShowParentDrawer('addSale') ? 'block' : 'none' }}>
          <SaleForm onClose={() => closeDrawer()} />
        </div>

        {/* Sale Form - Edit */}
        <div style={{ display: shouldShowParentDrawer('edit-sale') ? 'block' : 'none' }}>
          <SaleForm key={`edit-sale-${drawerData?.sale?.id ?? 'none'}-${drawerData?.convertToFinal ? 'convert' : 'edit'}`} sale={drawerData?.sale} convertToFinal={drawerData?.convertToFinal} onClose={() => closeDrawer()} />
        </div>

        {/* Purchase Form - Add */}
        <div style={{ display: shouldShowParentDrawer('addPurchase') ? 'block' : 'none' }}>
          <PurchaseForm onClose={() => closeDrawer()} />
        </div>

        {/* Purchase Form - Edit */}
        <div style={{ display: shouldShowParentDrawer('edit-purchase') ? 'block' : 'none' }}>
          <PurchaseForm purchase={drawerData?.purchase} onClose={() => closeDrawer()} />
        </div>
      </>
    );
  };

  // Render content based on active drawer
  const renderContent = () => {
    // Render all forms but hide inactive ones - this preserves state
    return (
      <>
        {/* Sale Form - Add */}
        <div style={{ display: shouldShowParentDrawer('addSale') ? 'block' : 'none' }}>
          <SaleForm onClose={() => closeDrawer()} />
        </div>

        {/* Sale Form - Edit */}
        <div style={{ display: shouldShowParentDrawer('edit-sale') ? 'block' : 'none' }}>
          <SaleForm key={`edit-sale-${drawerData?.sale?.id ?? 'none'}-${drawerData?.convertToFinal ? 'convert' : 'edit'}`} sale={drawerData?.sale} convertToFinal={drawerData?.convertToFinal} onClose={() => closeDrawer()} />
        </div>

        {/* Purchase Form - Add */}
        <div style={{ display: shouldShowParentDrawer('addPurchase') ? 'block' : 'none' }}>
          <PurchaseForm onClose={() => closeDrawer()} />
        </div>

        {/* Purchase Form - Edit */}
        <div style={{ display: shouldShowParentDrawer('edit-purchase') ? 'block' : 'none' }}>
          <PurchaseForm purchase={drawerData?.purchase} onClose={() => closeDrawer()} />
        </div>

        {/* Product Form - Add */}
        {activeDrawer === 'addProduct' && (
          <EnhancedProductForm 
            duplicateFromProductId={
              drawerData?.duplicateFrom?.uuid ?? drawerData?.duplicateFrom?.id ?? undefined
            }
            onCancel={() => closeDrawer()}
            onSave={(product?: any) => {
              if (hasNestedProductDrawer && product && setCreatedProduct) {
                setCreatedProduct(product);
                closeDrawer();
              } else {
                toast.success('Product created successfully');
                closeDrawer();
                window.dispatchEvent(new CustomEvent('products-updated'));
              }
            }}
          />
        )}

        {/* Product Form - Edit */}
        {activeDrawer === 'edit-product' && (
          <EnhancedProductForm 
            product={drawerData?.product}
            onCancel={() => closeDrawer()}
            onSave={() => {
              toast.success('Product updated successfully');
              closeDrawer();
              window.dispatchEvent(new CustomEvent('products-updated'));
            }}
          />
        )}

        {/* Contact Form - Only mount when active; wrapper ensures scroll works */}
        {activeDrawer === 'addContact' && (
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <ContactFormContent onClose={() => closeDrawer()} />
          </div>
        )}

      </>
    );
  };

  // Render nested drawers: When Contact is opened from Sale/Purchase, render both drawers
  if (hasNestedDrawer) {
    // Render parent drawer (Sale/Purchase) with lower z-index
    const parentIsSale = parentDrawer === 'addSale' || parentDrawer === 'edit-sale';
    const parentIsPurchase = parentDrawer === 'addPurchase';
    const parentContentClasses = "border-l border-border bg-background text-foreground p-0 gap-0 h-full !max-w-none w-full lg:w-[1200px] lg:max-w-[1200px] !z-[60] overflow-y-auto";
    
    return (
      <>
        {/* Parent Drawer (Sale/Purchase) - Lower z-index, stays open */}
        <Sheet open={true} onOpenChange={() => {}}>
          <SheetContent side={side} className={parentContentClasses}>
            <SheetHeader className="sr-only">
              <SheetTitle>
                {parentIsSale ? 'Add Sale' : parentIsPurchase ? 'Add Purchase' : 'Drawer'}
              </SheetTitle>
              <SheetDescription className="sr-only">
                {parentIsSale ? 'Create a new sale' : parentIsPurchase ? 'Create a new purchase' : 'Drawer content'}
              </SheetDescription>
            </SheetHeader>
            {renderParentContent()}
          </SheetContent>
        </Sheet>
        
        {/* Child Drawer (Contact) - Higher z-index, appears on top, proper overflow */}
        <Sheet open={isOpen} onOpenChange={handleOpenChange}>
          <SheetContent side={side} className={contentClasses}>
            <SheetHeader className="sr-only">
              <SheetTitle>Add Contact</SheetTitle>
              <SheetDescription className="sr-only">Create a new contact</SheetDescription>
            </SheetHeader>
            {activeDrawer === 'addContact' && (
              <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                <ContactFormContent onClose={() => closeDrawer()} />
              </div>
            )}
          </SheetContent>
        </Sheet>
        
        {/* Global Packing Modal - Only when Enable Packing is ON */}
        {enablePacking && packingModalData && (
          <PackingEntryModal
            open={packingModalOpen || false}
            onOpenChange={(open) => {
              if (!open) closePackingModal?.();
            }}
            onSave={(details) => {
              if (packingModalData.onSave) {
                packingModalData.onSave(details);
              }
              closePackingModal?.();
            }}
            initialData={packingModalData.initialData}
            productName={packingModalData.productName}
          />
        )}
      </>
    );
  }

  // Nested Product drawer: Sale/Purchase stays open, Add Product on top
  if (hasNestedProductDrawer) {
    const parentIsSale = parentDrawer === 'addSale' || parentDrawer === 'edit-sale';
    const parentIsPurchase = parentDrawer === 'addPurchase' || parentDrawer === 'edit-purchase';
    const parentContentClasses = "border-l border-border bg-background text-foreground p-0 gap-0 h-full !max-w-none w-full lg:w-[1200px] lg:max-w-[1200px] !z-[60] overflow-y-auto";
    return (
      <>
        <Sheet open={true} onOpenChange={() => {}}>
          <SheetContent side={side} className={parentContentClasses}>
            <SheetHeader className="sr-only">
              <SheetTitle>{parentIsSale ? 'Sale' : parentIsPurchase ? 'Purchase' : 'Drawer'}</SheetTitle>
              <SheetDescription className="sr-only">{parentIsSale ? 'Sale form' : parentIsPurchase ? 'Purchase form' : 'Drawer'}</SheetDescription>
            </SheetHeader>
            {renderParentContent()}
          </SheetContent>
        </Sheet>
        <Sheet open={isOpen} onOpenChange={handleOpenChange}>
          <SheetContent side={side} className={contentClasses}>
            <SheetHeader className="sr-only">
              <SheetTitle>Add Product</SheetTitle>
              <SheetDescription className="sr-only">Create a new product</SheetDescription>
            </SheetHeader>
            <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
              <EnhancedProductForm
                sourceContext={parentIsPurchase ? 'purchase' : parentIsSale ? 'sale' : 'default'}
                onCancel={() => closeDrawer()}
                onSave={(product?: any) => {
                  if (product && setCreatedProduct) {
                    setCreatedProduct(product);
                    closeDrawer();
                  } else {
                    toast.success('Product created successfully');
                    closeDrawer();
                    window.dispatchEvent(new CustomEvent('products-updated'));
                  }
                }}
              />
            </div>
          </SheetContent>
        </Sheet>
        {enablePacking && packingModalData && (
          <PackingEntryModal
            open={packingModalOpen || false}
            onOpenChange={(open) => { if (!open) closePackingModal?.(); }}
            onSave={(details) => { packingModalData.onSave?.(details); closePackingModal?.(); }}
            initialData={packingModalData.initialData}
            productName={packingModalData.productName}
          />
        )}
      </>
    );
  }
  
  // Single drawer (normal case) — unmount when closed so Radix scroll-lock cannot stick on body
  if (!isOpen) {
    return (
      <>
        {enablePacking && packingModalData && (
          <PackingEntryModal
            open={packingModalOpen || false}
            onOpenChange={(open) => {
              if (!open) closePackingModal?.();
            }}
            onSave={(details) => {
              if (packingModalData.onSave) {
                packingModalData.onSave(details);
              }
              closePackingModal?.();
            }}
            initialData={packingModalData.initialData}
            productName={packingModalData.productName}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Sheet open onOpenChange={handleOpenChange}>
        <SheetContent side={side} className={contentClasses}>
          {/* Hidden title for accessibility */}
          <SheetHeader className="sr-only">
            <SheetTitle>
              {isSale ? 'Add Sale' : 
               isPurchase ? 'Add Purchase' : 
               isProduct ? 'Add Product' : 
               isContact ? 'Add Contact' : 
               'Drawer'}
            </SheetTitle>
            <SheetDescription className="sr-only">
              {isSale ? 'Create a new sale' : 
               isPurchase ? 'Create a new purchase' : 
               isProduct ? 'Create a new product' : 
               isContact ? 'Create a new contact' : 
               'Drawer content'}
            </SheetDescription>
          </SheetHeader>
          <div className={isProduct || isContact ? "flex flex-col flex-1 min-h-0 overflow-hidden" : ""}>
            {renderContent()}
          </div>
        </SheetContent>
      </Sheet>
      
      {/* Global Packing Modal - Only when Enable Packing is ON */}
      {enablePacking && packingModalData && (
        <PackingEntryModal
          open={packingModalOpen || false}
          onOpenChange={(open) => {
            if (!open) closePackingModal?.();
          }}
          onSave={(details) => {
            if (packingModalData.onSave) {
              packingModalData.onSave(details);
            }
            closePackingModal?.();
          }}
          initialData={packingModalData.initialData}
          productName={packingModalData.productName}
        />
      )}
    </>
  );
};

// Contact Form Component
const ContactFormContent = ({ onClose }: { onClose: () => void }) => {
  const { drawerContactType, setCreatedContactId, parentDrawer, drawerPrefillName, drawerPrefillPhone, drawerData } = useNavigation();
  const { companyId, branchId, user } = useSupabase();
  const { accountingSettings } = useSettings();
  const multiCurrencyEnabled = accountingSettings?.multiCurrencyEnabled === true;
  const [contactRoles, setContactRoles] = useState<{
    customer: boolean;
    supplier: boolean;
    worker: boolean;
    money_exchange: boolean;
  }>({
    customer: drawerContactType === 'customer' ? true : false,
    supplier: drawerContactType === 'supplier' ? true : false,
    worker: drawerContactType === 'worker' ? true : false,
    money_exchange: false,
  });
  const [workerType, setWorkerType] = useState<string>(DEFAULT_WORKER_ROLES[0].value);
  const [saving, setSaving] = useState(false);
  const [country, setCountry] = useState<string>('pk');
  const [selectedGroupId, setSelectedGroupId] = useState<string>('none');
  const [contactGroups, setContactGroups] = useState<any[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [editContact, setEditContact] = useState<any>(null);
  const [loadingEdit, setLoadingEdit] = useState(false);

  const [prefillName, setPrefillName] = useState<string>(drawerPrefillName || '');
  const [prefillPhone, setPrefillPhone] = useState<string>(drawerPrefillPhone || '');

  // When opening for edit: prefill from list row immediately, then fetch full contact
  React.useEffect(() => {
    if (drawerPrefillName) setPrefillName(drawerPrefillName);
    if (drawerPrefillPhone) setPrefillPhone(drawerPrefillPhone);
    if (drawerData?.contact) {
      setPrefillName(drawerData.contact.name ?? drawerPrefillName ?? '');
      setPrefillPhone(drawerData.contact.phone ?? drawerPrefillPhone ?? '');
    }
  }, [drawerPrefillName, drawerPrefillPhone, drawerData?.contact]);

  // Fetch full contact when editing (Step B: Edit Contact form pre-population)
  React.useEffect(() => {
    const uuid = drawerData?.contact?.uuid;
    if (!uuid) {
      setEditContact(null);
      return;
    }
    let cancelled = false;
    setLoadingEdit(true);
    contactService.getContact(uuid)
      .then((data) => {
        if (!cancelled) {
          setEditContact(data);
          const t = data?.type;
          if (t === 'worker') {
            setContactRoles({ customer: false, supplier: false, worker: true, money_exchange: false });
            setWorkerType((data?.worker_role || 'dyer') as string);
          } else if (t === 'money_exchange') {
            setContactRoles({ customer: false, supplier: false, worker: false, money_exchange: true });
          } else if (t === 'supplier') {
            setContactRoles({ customer: false, supplier: true, worker: false, money_exchange: false });
          } else if (t === 'both') {
            setContactRoles({ customer: true, supplier: true, worker: false, money_exchange: false });
          } else {
            setContactRoles({ customer: true, supplier: false, worker: false, money_exchange: false });
          }
          const c = (data?.country || '').toString().toLowerCase();
          setCountry(c.includes('india') ? 'in' : c.includes('bangladesh') ? 'bd' : 'pk');
          setSelectedGroupId(data?.group_id || 'none');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setEditContact(null);
          toast.error('Could not load contact details. You can still edit name and phone.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingEdit(false);
      });
    return () => { cancelled = true; };
  }, [drawerData?.contact?.uuid]);

  React.useEffect(() => {
    if (drawerContactType) {
      setContactRoles({
        customer: drawerContactType === 'customer' ? true : false,
        supplier: drawerContactType === 'supplier' ? true : false,
        worker: drawerContactType === 'worker' ? true : false,
        money_exchange: false,
      });
      if (drawerContactType === 'worker') setWorkerType('dyer');
    } else if (!drawerData?.contact) {
      setContactRoles({
        customer: false,
        supplier: false,
        worker: false,
        money_exchange: false,
      });
    }
  }, [drawerContactType, drawerData?.contact]);

  // Load contact groups based on selected roles
  React.useEffect(() => {
    const loadGroups = async () => {
      if (!companyId) return;
      
      setLoadingGroups(true);
      try {
        // Load groups for Customer and Supplier only (Worker doesn't need groups)
        const groupPromises: Promise<any[]>[] = [];
        if (contactRoles.customer) {
          groupPromises.push(contactGroupService.getAllGroups(companyId, 'customer'));
        }
        if (contactRoles.supplier) {
          groupPromises.push(contactGroupService.getAllGroups(companyId, 'supplier'));
        }
        
        // If no Customer/Supplier selected, don't load groups (Worker doesn't use groups)
        if (groupPromises.length === 0) {
          setContactGroups([]);
          setLoadingGroups(false);
          return;
        }
        
        const groupsArrays = await Promise.all(groupPromises);
        const allGroups = groupsArrays.flat();
        console.log('[CONTACT FORM] Loaded contact groups:', allGroups.length, allGroups);
        setContactGroups(allGroups);
      } catch (error: any) {
        // Service already handles 404/table not found gracefully
        // Just set empty array, form will work without groups
        setContactGroups([]);
      } finally {
        setLoadingGroups(false);
      }
    };
    
    loadGroups();
  }, [companyId, contactRoles.customer, contactRoles.supplier]);
  
  // Role toggle: Customer/Supplier vs Worker vs money_exchange (exclusive FX agent)
  const handleRoleToggle = (role: 'customer' | 'supplier' | 'worker' | 'money_exchange') => {
    if (role === 'money_exchange') {
      const on = !contactRoles.money_exchange;
      setContactRoles({
        customer: false,
        supplier: false,
        worker: false,
        money_exchange: on,
      });
      return;
    }
    if (role === 'customer' || role === 'supplier') {
      const newValue = !contactRoles[role];
      setContactRoles({
        ...contactRoles,
        [role]: newValue,
        worker: false,
        money_exchange: false,
      });
    } else if (role === 'worker') {
      const newWorkerValue = !contactRoles.worker;
      if (newWorkerValue) {
        setContactRoles({
          customer: false,
          supplier: false,
          worker: true,
          money_exchange: false,
        });
      } else {
        setContactRoles({
          ...contactRoles,
          worker: false,
        });
      }
    }
  };

  // Determine primary type for database (backward compatibility)
  const getPrimaryType = (): 'customer' | 'supplier' | 'worker' | 'both' | 'money_exchange' => {
    if (contactRoles.money_exchange) return 'money_exchange';
    if (contactRoles.worker) return 'worker';
    if (contactRoles.customer && contactRoles.supplier) return 'both';
    if (contactRoles.supplier) return 'supplier';
    return 'customer';
  };

  // Handle form submission
  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!companyId || !user) {
      toast.error('Company ID or user not found. Please login again.');
      return;
    }

    setSaving(true);

    try {
      const formData = new FormData(e.currentTarget);
      const primaryType = getPrimaryType();
      
      // Check for duplicate contact by phone number (mandatory check)
      const existingContacts = await contactService.getAllContacts(companyId);
      const phone = formData.get('mobile') as string;
      const name = formData.get('business-name') as string;
      
      const editingId = drawerData?.contact?.uuid;
      if (phone && phone.trim() !== '') {
        const duplicateByPhone = existingContacts.find(
          (c: any) => c.id !== editingId && (c.phone === phone || c.mobile === phone)
        );
        if (duplicateByPhone) {
          toast.error(`Ye number pehle se "${duplicateByPhone.name}" ke naam se save hai`);
          setSaving(false);
          return;
        }
      }
      
      const parseMoney = (v: FormDataEntryValue | null) =>
        parseFloat(String(v ?? '').trim() || '0') || 0;
      const openingBalRaw = formData.get('opening-balance');
      const supplierOpeningRaw = formData.get('supplier-opening-balance');

      const contactData: Record<string, unknown> = {
        company_id: companyId,
        type: primaryType,
        name: (name || '').trim() || undefined,
        phone: (phone || '').trim() || undefined,
        email: (formData.get('email') as string)?.trim() || undefined,
        address: (formData.get('address') as string)?.trim() || undefined,
        city: (formData.get('city') as string)?.trim() || undefined,
        country: country === 'pk' ? 'Pakistan' : country === 'in' ? 'India' : country === 'bd' ? 'Bangladesh' : 'Pakistan',
        credit_limit: parseFloat(formData.get('credit-limit') as string) || 0,
        payment_terms: parseInt(formData.get('pay-term') as string) || 0,
        tax_number: (formData.get('tax-id') as string)?.trim() || undefined,
        notes: (formData.get('notes') as string)?.trim() || undefined,
        created_by: user.id,
      };

      if (primaryType === 'worker') {
        contactData.opening_balance = parseMoney(openingBalRaw);
      } else if (primaryType === 'customer') {
        contactData.opening_balance = parseMoney(openingBalRaw);
        contactData.supplier_opening_balance = 0;
      } else if (primaryType === 'supplier') {
        contactData.opening_balance = 0;
        contactData.supplier_opening_balance = parseMoney(supplierOpeningRaw);
      } else if (primaryType === 'both') {
        contactData.opening_balance = parseMoney(openingBalRaw);
        contactData.supplier_opening_balance = parseMoney(supplierOpeningRaw);
      }
      // Only set branch_id when it's a valid UUID (not "all" or other non-UUID)
      if (branchId && branchId !== 'all' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(branchId)) {
        contactData.branch_id = branchId;
      }
      if (selectedGroupId && selectedGroupId !== 'none') contactData.group_id = selectedGroupId;

      if (contactRoles.supplier) {
        const supplierBusinessName = (formData.get('supplier-business-name') as string)?.trim();
        const contactPerson = (formData.get('contact-person') as string)?.trim();
        const ntn = (formData.get('supplier-ntn') as string)?.trim();
        const payableAccount = (formData.get('supplier-payable-account') as string)?.trim();
        if (supplierBusinessName) contactData.business_name = supplierBusinessName;
        if (contactPerson) contactData.contact_person = contactPerson;
        if (ntn) contactData.ntn = ntn;
        if (payableAccount) contactData.payable_account_id = payableAccount;
      }
      if (contactRoles.worker && workerType) contactData.worker_role = workerType;
      if (contactRoles.worker) {
        contactData.worker_default_rate = parseMoney(formData.get('worker-rate'));
      }

      // Remove undefined so API doesn't receive invalid payload
      Object.keys(contactData).forEach((k) => {
        if (contactData[k] === undefined) delete contactData[k];
      });
      if (!contactData.name) {
        toast.error('Contact name is required');
        setSaving(false);
        return;
      }

      // Validate at least one role is selected
      if (!contactRoles.customer && !contactRoles.supplier && !contactRoles.worker && !contactRoles.money_exchange) {
        toast.error('Please select at least one contact role');
        setSaving(false);
        return;
      }
      if (contactRoles.worker && !workerType?.trim()) {
        toast.error('Please select or add a worker role');
        setSaving(false);
        return;
      }

      const editingContactId = drawerData?.contact?.uuid;
      if (editingContactId) {
        await contactService.updateContact(editingContactId, contactData as any);
        toast.success('Contact updated successfully');
        setCreatedContactId?.(editingContactId);
        onClose();
        setSaving(false);
        return;
      }

      const createdContact = await contactService.createContact(contactData);
      
      // Use the ID from the created contact (uuid)
      const contactId = createdContact?.id || (createdContact as { uuid?: string })?.uuid;
      const contactName = (createdContact as { name?: string })?.name || (formData.get('business-name') as string) || '';

      // Opening balances: contactService.createContact triggers GL sync (AR/AP/worker) + worker operational balance.

      if (contactId && setCreatedContactId) {
        // Determine the contact type for filtering
        let contactTypeForFilter: 'customer' | 'supplier' | 'both' | null = null;
        if (contactRoles.customer && contactRoles.supplier) {
          contactTypeForFilter = 'both';
        } else if (contactRoles.customer) {
          contactTypeForFilter = 'customer';
        } else if (contactRoles.supplier) {
          contactTypeForFilter = 'supplier';
        }
        
        setCreatedContactId(contactId, contactTypeForFilter);
        console.log('[CONTACT FORM] Created contact ID stored:', contactId, 'Type:', contactTypeForFilter);
      }

      toast.success('Contact created successfully!');
      
      // CRITICAL FIX: If opened from Sale/Purchase, keep drawer open briefly to allow auto-select
      // The parent form's useEffect will detect createdContactId and reload immediately
      if (parentDrawer === 'addSale' || parentDrawer === 'addPurchase') {
        // Keep drawer open for a moment to allow parent form to detect createdContactId
        // Parent form will reload and auto-select, then we can close
        setTimeout(() => {
          onClose();
        }, 500); // Delay to allow parent form to process createdContactId
      } else {
        // If not from Sale/Purchase, close immediately
        onClose();
      }
    } catch (error: any) {
      console.error('Error creating contact:', error);
      toast.error(error.message || 'Failed to create contact. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  const ec = editContact;
  const defaultName = ec?.name ?? prefillName ?? '';
  const defaultPhone = ec?.phone ?? ec?.mobile ?? prefillPhone ?? '';
  const defaultEmail = ec?.email ?? '';
  const defaultAddress = ec?.address ?? '';
  const defaultCity = ec?.city ?? '';
  const defaultOpeningBalance = ec?.opening_balance != null ? String(ec.opening_balance) : '';
  const defaultCreditLimit = ec?.credit_limit != null ? String(ec.credit_limit) : '';
  const defaultPayTerm = ec?.payment_terms != null ? String(ec.payment_terms) : '';
  const defaultTaxId = ec?.tax_number ?? ec?.ntn ?? '';
  const defaultNotes = ec?.notes ?? '';
  const defaultSupplierBusinessName = ec?.business_name ?? '';
  const defaultContactPerson = ec?.contact_person ?? '';
  const defaultSupplierNtn = ec?.ntn ?? '';
  const defaultSupplierOpeningBalance = ec?.supplier_opening_balance != null ? String(ec.supplier_opening_balance) : '';
  const defaultWorkerRate =
    ec?.worker_default_rate != null && ec.worker_default_rate !== ''
      ? String(Number(ec.worker_default_rate))
      : '';

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="p-6 border-b border-border bg-muted/40 sticky top-0 z-10 shrink-0">
        <h2 className="text-xl font-bold text-foreground">{drawerData?.contact ? 'Edit Contact' : 'Add New Contact'}</h2>
        <p className="text-sm text-muted-foreground mt-1">
          {drawerData?.contact
            ? (loadingEdit ? 'Loading contact...' : 'Update contact details below.')
            : drawerContactType 
            ? `Adding new ${drawerContactType}. Other roles disabled.`
            : 'Select contact roles (Customer/Supplier can be combined, Worker is separate)'}
        </p>
        
        {/* Contact Roles - Segmented Buttons / Pills */}
        <div className="mt-4">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => handleRoleToggle('customer')}
              disabled={(drawerContactType && drawerContactType !== 'customer') || contactRoles.money_exchange}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                contactRoles.customer
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30 border-2 border-blue-500'
                  : 'bg-muted text-muted-foreground hover:bg-accent border-2 border-border'
              } ${
                ((drawerContactType && drawerContactType !== 'customer') || contactRoles.money_exchange)
                  ? 'opacity-50 cursor-not-allowed' 
                  : ''
              }`}
              title={
                contactRoles.money_exchange
                  ? 'Clear Money Exchange first'
                  : (drawerContactType && drawerContactType !== 'customer')
                  ? `Only ${drawerContactType} role allowed from this context`
                  : 'Select Customer role'
              }
            >
              <span className={`w-2 h-2 rounded-full ${contactRoles.customer ? 'bg-white' : 'bg-blue-500'}`}></span>
              Customer
            </button>
            
            <button
              type="button"
              onClick={() => handleRoleToggle('supplier')}
              disabled={(drawerContactType && drawerContactType !== 'supplier') || contactRoles.money_exchange}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                contactRoles.supplier
                  ? 'bg-purple-600 text-foreground shadow-lg shadow-purple-500/30 border-2 border-purple-500'
                  : 'bg-muted text-muted-foreground hover:bg-accent border-2 border-border'
              } ${
                ((drawerContactType && drawerContactType !== 'supplier') || contactRoles.money_exchange)
                  ? 'opacity-50 cursor-not-allowed' 
                  : ''
              }`}
              title={
                contactRoles.money_exchange
                  ? 'Clear Money Exchange first'
                  : (drawerContactType && drawerContactType !== 'supplier')
                  ? `Only ${drawerContactType} role allowed from this context`
                  : 'Select Supplier role'
              }
            >
              <span className={`w-2 h-2 rounded-full ${contactRoles.supplier ? 'bg-white' : 'bg-purple-500'}`}></span>
              Supplier
            </button>
            
            <button
              type="button"
              onClick={() => handleRoleToggle('worker')}
              disabled={contactRoles.customer || contactRoles.supplier || contactRoles.money_exchange || (drawerContactType && drawerContactType !== 'worker')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                contactRoles.worker
                  ? 'bg-green-600 text-foreground shadow-lg shadow-green-500/30 border-2 border-green-500'
                  : 'bg-muted text-muted-foreground hover:bg-accent border-2 border-border'
              } ${
                (contactRoles.customer || contactRoles.supplier || contactRoles.money_exchange || (drawerContactType && drawerContactType !== 'worker'))
                  ? 'opacity-50 cursor-not-allowed' 
                  : ''
              }`}
              title={
                (drawerContactType && drawerContactType !== 'worker')
                  ? `Only ${drawerContactType} role allowed from this context`
                  : (contactRoles.customer || contactRoles.supplier || contactRoles.money_exchange)
                  ? 'Worker cannot be selected with Customer, Supplier, or Money Exchange'
                  : 'Select Worker role'
              }
            >
              <span className={`w-2 h-2 rounded-full ${contactRoles.worker ? 'bg-white' : 'bg-green-500'}`}></span>
              Worker
            </button>

            {multiCurrencyEnabled && (
              <button
                type="button"
                onClick={() => handleRoleToggle('money_exchange')}
                disabled={!!drawerContactType}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                  contactRoles.money_exchange
                    ? 'bg-amber-600 text-white shadow-lg shadow-amber-500/30 border-2 border-amber-500'
                    : 'bg-muted text-muted-foreground hover:bg-accent border-2 border-border'
                } ${drawerContactType ? 'opacity-50 cursor-not-allowed' : ''}`}
                title="Money exchange agent (AP under 2000 for dual-credit FX)"
              >
                <span className={`w-2 h-2 rounded-full ${contactRoles.money_exchange ? 'bg-white' : 'bg-amber-500'}`}></span>
                Money Exchange
              </button>
            )}
          </div>
          
          {!contactRoles.customer && !contactRoles.supplier && !contactRoles.worker && !contactRoles.money_exchange && (
            <p className="text-xs text-red-400 mt-2">Please select at least one role</p>
          )}
          
          {(contactRoles.customer || contactRoles.supplier) && contactRoles.worker && (
            <p className="text-xs text-amber-400 mt-2">Worker cannot be selected with Customer or Supplier</p>
          )}
        </div>
      </div>

      <form
        key={`contact-form-${ec?.id ?? 'new'}`}
        onSubmit={handleContactSubmit}
        className="flex-1 min-h-0 overflow-y-auto p-6 space-y-6"
      >
        {/* Contact Group / Category - Show for Customer and Supplier, hide for Worker */}
        {(contactRoles.customer || contactRoles.supplier) && (
          <div className="space-y-2">
            <Label htmlFor="contact-group" className="text-gray-200">Group / Category (Optional)</Label>
            {loadingGroups ? (
              <div className="text-sm text-muted-foreground py-2">Loading groups...</div>
            ) : contactGroups.length > 0 ? (
              <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
                <SelectTrigger className="bg-input-background border-border text-foreground">
                  <SelectValue placeholder="Select a group (optional)" />
                </SelectTrigger>
                <SelectContent className="bg-input-background border-border text-foreground max-h-[280px] overflow-y-auto">
                  <SelectItem value="none">None</SelectItem>
                  {contactGroups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name} {group.type && `(${group.type})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="text-sm text-muted-foreground italic py-2 px-3 bg-muted/50 rounded border border-border">
                No groups available. Groups will appear here once created in settings.
              </div>
            )}
            <p className="text-xs text-muted-foreground">Organize contacts into groups for easier management</p>
          </div>
        )}

        {/* Basic Info */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider">Basic Information</h3>
          
          {/* Name field - role-based labels */}
          <div className="space-y-2">
            <Label htmlFor="business-name" className="text-gray-200">
              {contactRoles.worker 
                ? 'Worker Name *' 
                : contactRoles.customer && !contactRoles.supplier
                ? 'Business / Person Name *'
                : contactRoles.supplier && !contactRoles.customer
                ? 'Business Name *'
                : 'Business / Contact Name *'}
            </Label>
            <Input 
              id="business-name"
              name="business-name"
              defaultValue={defaultName}
              placeholder={
                contactRoles.worker 
                  ? 'e.g. Ahmed Ali' 
                  : contactRoles.supplier
                  ? 'e.g. ABC Trading Company'
                  : 'e.g. Ahmed Retailers'
              } 
              className="bg-input-background border-border text-foreground" 
              required 
            />
          </div>

          {/* Contact Person - Only for Suppliers */}
          {contactRoles.supplier && (
            <div className="space-y-2">
              <Label htmlFor="contact-person" className="text-gray-200">Contact Person</Label>
            <Input 
              id="contact-person"
              name="contact-person"
              defaultValue={defaultContactPerson}
              placeholder="e.g. John Doe (optional)" 
              className="bg-input-background border-border text-foreground" 
            />
              <p className="text-xs text-muted-foreground">Primary contact person at supplier</p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="mobile" className="text-gray-200">Mobile Number *</Label>
            <Input 
              id="mobile"
              name="mobile"
              defaultValue={defaultPhone}
              placeholder="+92 300 1234567" 
              className="bg-input-background border-border text-foreground" 
              required 
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-200">Email Address</Label>
            <Input 
              id="email"
              name="email"
              type="email"
              defaultValue={defaultEmail}
              placeholder="contact@business.com" 
              className="bg-input-background border-border text-foreground" 
            />
          </div>
        </div>

        {/* Supplier Extra Business Details - Collapsible */}
        {contactRoles.supplier && (
          <Accordion type="single" collapsible className="border border-purple-500/30 rounded-lg bg-purple-500/5">
            <AccordionItem value="supplier-details" className="border-none">
              <AccordionTrigger className="px-4 hover:bg-purple-500/10">
                <span className="text-sm font-semibold text-purple-400 uppercase tracking-wider flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                  Supplier Business Details (Optional)
                </span>
              </AccordionTrigger>
              {/* forceMount: keep supplier detail inputs in DOM when accordion is collapsed */}
              <AccordionContent forceMount className="px-4 pb-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="supplier-business-name" className="text-gray-200">Business Name</Label>
                    <Input 
                      id="supplier-business-name"
                      name="supplier-business-name"
                      defaultValue={defaultSupplierBusinessName}
                      placeholder="e.g. ABC Trading Company" 
                      className="bg-input-background border-border text-foreground" 
                    />
                    <p className="text-xs text-muted-foreground">Legal business name (if different from contact name)</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="supplier-ntn" className="text-gray-200">NTN / Tax ID</Label>
                    <Input 
                      id="supplier-ntn"
                      name="supplier-ntn"
                      defaultValue={defaultSupplierNtn}
                      placeholder="NTN-1234567" 
                      className="bg-input-background border-border text-foreground" 
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="supplier-payable-account" className="text-gray-200">Payable Account</Label>
                    <Select name="supplier-payable-account" defaultValue="">
                      <SelectTrigger className="bg-input-background border-border text-foreground">
                        <SelectValue placeholder="Select account (optional)" />
                      </SelectTrigger>
                      <SelectContent className="bg-input-background border-border text-foreground">
                        {/* Accounts will be loaded dynamically if needed */}
                        {/* Note: Empty Select will show placeholder when no value selected */}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">Default account for supplier payments</p>
                  </div>
                  
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}

        {/* Worker Role (category) – Only these values; matches Studio assignment filter */}
        {contactRoles.worker && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 space-y-4">
            <h3 className="text-sm font-semibold text-green-400 uppercase tracking-wider flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              Worker Role (required)
            </h3>
            <p className="text-xs text-muted-foreground">Used for Studio: Dyeing / Stitching / Handwork task assignment.</p>
            {companyId ? (
              <WorkerRoleCombobox
                companyId={companyId}
                value={workerType}
                onChange={setWorkerType}
              />
            ) : (
              <p className="text-xs text-amber-400">Company context required to load roles.</p>
            )}

            {/* Worker Rate */}
            <div className="space-y-2">
              <Label htmlFor="worker-rate" className="text-gray-200">Rate / Payment</Label>
              <Input
                id="worker-rate"
                name="worker-rate"
                type="number"
                min={0}
                step="0.01"
                key={`worker-rate-${drawerData?.contact?.uuid ?? 'new'}-${drawerData?.contact?.uuid ? (loadingEdit ? 'loading' : 'ready') : 'new'}`}
                defaultValue={defaultWorkerRate}
                placeholder="e.g. 500 per piece / day"
                className="bg-popover border-border text-popover-foreground"
              />
              <p className="text-xs text-muted-foreground">Standard payment rate for this worker</p>
            </div>
          </div>
        )}

        {/* Advanced Details Accordion */}
        <Accordion type="multiple" className="border border-border rounded-lg">
          <AccordionItem value="financial" className="border-b border-border">
            <AccordionTrigger className="px-4 hover:bg-accent/50">
              <span className="text-sm font-medium">Financial Details</span>
            </AccordionTrigger>
            {/* forceMount: opening-balance / credit-limit must submit even when accordion is collapsed */}
            <AccordionContent forceMount className="px-4 pb-4">
              <div className="space-y-4">
                {contactRoles.worker && (
                  <div className="space-y-2">
                    <Label htmlFor="opening-balance" className="text-gray-200">
                      Worker opening balance
                    </Label>
                    <Input
                      id="opening-balance"
                      name="opening-balance"
                      type="number"
                      defaultValue={defaultOpeningBalance}
                      placeholder="0.00"
                      className="bg-input-background border-border text-foreground"
                    />
                    <p className="text-xs text-muted-foreground">
                      Posts to worker payable / advance (2010 / 1180) via opening journal sync.
                    </p>
                  </div>
                )}

                {contactRoles.customer && !contactRoles.supplier && !contactRoles.worker && (
                  <div className="space-y-2">
                    <Label htmlFor="opening-balance" className="text-gray-200">
                      Customer opening (receivable)
                    </Label>
                    <p className="text-xs text-muted-foreground">You will receive from this contact</p>
                    <Input
                      id="opening-balance"
                      name="opening-balance"
                      type="number"
                      defaultValue={defaultOpeningBalance}
                      placeholder="0.00"
                      className="bg-input-background border-border text-foreground"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      Accounting entry: Dr Accounts Receivable (1100) · Cr Owner Capital (3000)
                    </p>
                  </div>
                )}

                {contactRoles.supplier && !contactRoles.customer && !contactRoles.worker && (
                  <div className="space-y-2">
                    <Label htmlFor="supplier-opening-balance" className="text-gray-200">
                      Supplier opening (payable)
                    </Label>
                    <p className="text-xs text-muted-foreground">You will pay to this contact</p>
                    <Input
                      id="supplier-opening-balance"
                      name="supplier-opening-balance"
                      type="number"
                      defaultValue={defaultSupplierOpeningBalance}
                      placeholder="0.00"
                      className="bg-input-background border-border text-foreground"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      Accounting entry: Dr Owner Capital (3000) · Cr Accounts Payable (2000)
                    </p>
                  </div>
                )}

                {contactRoles.customer && contactRoles.supplier && !contactRoles.worker && (
                  <div className="space-y-4 rounded-lg border border-blue-500/25 bg-blue-500/5 p-3">
                    <p className="text-xs text-blue-200/90">
                      This contact is both customer and supplier — enter receivable and payable openings separately.
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="opening-balance-both" className="text-gray-200">
                        Customer opening (receivable / AR)
                      </Label>
                      <p className="text-xs text-muted-foreground">You will receive from this contact</p>
                      <Input
                        id="opening-balance-both"
                        name="opening-balance"
                        type="number"
                        defaultValue={defaultOpeningBalance}
                        placeholder="0.00"
                        className="bg-input-background border-border text-foreground"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Dr Accounts Receivable (1100) · Cr Owner Capital (3000)
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="supplier-opening-balance-both" className="text-gray-200">
                        Supplier opening (payable / AP)
                      </Label>
                      <p className="text-xs text-muted-foreground">You will pay to this contact</p>
                      <Input
                        id="supplier-opening-balance-both"
                        name="supplier-opening-balance"
                        type="number"
                        defaultValue={defaultSupplierOpeningBalance}
                        placeholder="0.00"
                        className="bg-input-background border-border text-foreground"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Dr Owner Capital (3000) · Cr Accounts Payable (2000)
                      </p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="credit-limit" className="text-gray-200">
                      Credit Limit
                    </Label>
                    <Input
                      id="credit-limit"
                      name="credit-limit"
                      type="number"
                      defaultValue={defaultCreditLimit}
                      placeholder="0.00"
                      className="bg-input-background border-border text-foreground"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pay-term" className="text-gray-200">
                      Payment Terms (Days)
                    </Label>
                    <Input
                      id="pay-term"
                      name="pay-term"
                      type="number"
                      defaultValue={defaultPayTerm}
                      placeholder="30"
                      className="bg-input-background border-border text-foreground"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Payment terms: number of days to settle invoices where applicable.</p>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="tax" className="border-b border-border">
            <AccordionTrigger className="px-4 hover:bg-accent/50">
              <span className="text-sm font-medium">Tax Information</span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="tax-id" className="text-gray-200">Tax / VAT ID</Label>
                  <Input 
                    id="tax-id"
                    name="tax-id"
                    defaultValue={defaultTaxId}
                    placeholder="NTN-1234567" 
                    className="bg-input-background border-border text-foreground" 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gst" className="text-gray-200">GST Number</Label>
                  <Input 
                    id="gst"
                    name="gst"
                    placeholder="GST-1234567890" 
                    className="bg-input-background border-border text-foreground" 
                  />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="address">
            <AccordionTrigger className="px-4 hover:bg-accent/50">
              <span className="text-sm font-medium">Billing Address</span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="address" className="text-gray-200">Street Address</Label>
                  <Textarea 
                    id="address"
                    name="address"
                    defaultValue={defaultAddress}
                    placeholder="Enter full address"
                    className="bg-input-background border-border text-foreground min-h-[80px]" 
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-gray-200">City</Label>
                    <Input 
                      id="city"
                      name="city"
                      defaultValue={defaultCity}
                      placeholder="Karachi" 
                      className="bg-input-background border-border text-foreground" 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country" className="text-gray-200">Country</Label>
                    <Select value={country} onValueChange={setCountry}>
                      <SelectTrigger className="bg-input-background border-border text-foreground">
                        <SelectValue placeholder="Pakistan" />
                      </SelectTrigger>
                      <SelectContent className="bg-input-background border-border text-foreground">
                        <SelectItem value="pk">Pakistan</SelectItem>
                        <SelectItem value="in">India</SelectItem>
                        <SelectItem value="bd">Bangladesh</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <div className="p-6 border-t border-border bg-muted/40 sticky bottom-0 z-10 flex gap-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onClose}
            className="flex-1 border-border text-muted-foreground"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={saving}
            className="flex-1 bg-blue-600 hover:bg-blue-500 text-foreground disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Contact'}
          </Button>
        </div>
      </form>
    </div>
  );
};

// User Form Component - REBUILT FROM SCRATCH
// UserFormContent removed - now using AddUserModal (centered modal) instead of drawer